/**
 * Default configurations for technical indicators
 *
 * These defaults provide sensible starting values for each indicator
 * and can be customized by users through the settings UI.
 */

export const INDICATOR_DEFAULTS = {
  macd: {
    enabled: false,
    fast: 12,
    slow: 26,
    signal: 9,
    ma_type: 'ema',  // 'sma', 'ema', 'wilders', 'weighted'
    source: 'close',  // 'close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'
    colors: {
      macd: '#06b6d4',        // Cyan
      signal: '#fb923c',       // Orange
      histogram: '#6b7280'     // Gray
    }
  },

  rsi: {
    enabled: false,
    period: 14,
    overbought: 70,
    oversold: 30,
    source: 'close',  // 'close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'
    colors: {
      rsi: '#a855f7',          // Purple
      overbought: '#ef4444',   // Red
      oversold: '#10b981',     // Green
      fill: 'rgba(168, 85, 247, 0.1)'  // Purple transparent
    }
  },

  bollinger: {
    enabled: false,
    period: 20,
    deviations_upper: 2.0,
    deviations_lower: 2.0,
    ma_type: 'sma',   // 'sma', 'ema', 'wilders', 'weighted'
    source: 'close',  // 'close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'
    colors: {
      upper: '#60a5fa',   // Blue
      middle: '#60a5fa',  // Blue
      lower: '#60a5fa'    // Blue
    }
  },

  volume: {
    enabled: false,
    ma_period: 20,
    ma_type: 'sma',  // 'sma', 'ema', 'wilders', 'weighted'
    colors: {
      bars: '#6b7280',     // Gray
      ma: '#3b82f6'        // Blue
    }
  },

  sma_50: {
    enabled: false,
    period: 50,
    displace: 0,
    source: 'close',  // 'close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'
    color: '#10b981'  // Green
  },

  sma_200: {
    enabled: false,
    period: 200,
    displace: 0,
    source: 'close',  // 'close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'
    color: '#ef4444'  // Red
  },

  sma_720: {
    enabled: false,
    period: 720,
    displace: 0,
    source: 'close',  // 'close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'
    color: '#f59e0b'  // Orange
  },

  ema: {
    enabled: false,
    period: 12,
    displace: 0,
    source: 'close',  // 'close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'
    color: '#8b5cf6'  // Purple
  },

  vwap: {
    enabled: false,
    color: '#06b6d4'  // Cyan
  },

  ac: {
    enabled: false,
    colors: {
      bullish: '#10b981',  // Green
      bearish: '#ef4444'   // Red
    }
  }
};

/**
 * Price source options for dropdowns
 * Used in indicator settings to select which price data to use for calculations
 */
export const PRICE_SOURCES = [
  { value: 'close', label: 'Close' },
  { value: 'open', label: 'Open' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
  { value: 'hl2', label: 'HL/2 (High + Low) / 2' },
  { value: 'hlc3', label: 'HLC/3 (High + Low + Close) / 3' },
  { value: 'ohlc4', label: 'OHLC/4 (Open + High + Low + Close) / 4' }
];

/**
 * Moving average type options for dropdowns
 * Used in MACD, Bollinger Bands, and Volume indicators
 */
export const MA_TYPES = [
  { value: 'sma', label: 'SMA (Simple)' },
  { value: 'ema', label: 'EMA (Exponential)' },
  { value: 'wilders', label: 'Wilders (Smoothed)' },
  { value: 'weighted', label: 'WMA (Weighted)' }
];

/**
 * Helper function to get default configuration for a specific indicator
 * @param {string} indicatorName - Name of the indicator
 * @returns {object} Default configuration or empty object if not found
 */
export const getIndicatorDefaults = (indicatorName) => {
  return INDICATOR_DEFAULTS[indicatorName] || {};
};

/**
 * Helper function to build indicator configuration for API request
 * Only includes enabled indicators with their custom parameters
 * @param {object} indicatorSettings - Object containing all indicator settings
 * @returns {object} Configuration object ready for API request
 */
export const buildIndicatorConfig = (indicatorSettings) => {
  const config = {};

  Object.keys(indicatorSettings).forEach(indicatorName => {
    const settings = indicatorSettings[indicatorName];

    // Only include enabled indicators
    if (settings.enabled) {
      // Build parameter object (exclude 'enabled' and 'colors' for API)
      const params = {};

      Object.keys(settings).forEach(key => {
        // Skip 'enabled' and 'colors' - these are frontend-only
        if (key !== 'enabled' && key !== 'colors' && key !== 'color') {
          params[key] = settings[key];
        }
      });

      config[indicatorName] = params;
    }
  });

  return config;
};

/**
 * Helper function to merge user settings with defaults
 * Ensures all required fields are present even if user customized only some
 * @param {object} userSettings - User's custom indicator settings
 * @returns {object} Complete settings with defaults filled in
 */
export const mergeWithDefaults = (userSettings = {}) => {
  const merged = {};

  // Start with all defaults
  Object.keys(INDICATOR_DEFAULTS).forEach(indicatorName => {
    merged[indicatorName] = {
      ...INDICATOR_DEFAULTS[indicatorName],
      ...(userSettings[indicatorName] || {})
    };
  });

  return merged;
};
